import React, { useState } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import { useCreateFieldOption, useUpdateFieldOption, useDeleteFieldOption } from "../../../hooks/CHAMPS/useFieldOptions";
import { toast } from "react-toastify";

const ManageFieldOptions = ({ show, setShow, champ }) => {
  const createOptionMutation = useCreateFieldOption();
  const updateOptionMutation = useUpdateFieldOption();
  const deleteOptionMutation = useDeleteFieldOption();

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ value: "", label: "" });
  const [originalOption, setOriginalOption] = useState(null);
  const [newOption, setNewOption] = useState({ value: "", label: "" });

  // Utiliser directement les options du champ au lieu d'un état local
  const options = champ?.field_options || [];

  const handleClose = () => {
    setShow(false);
    setEditingId(null);
    setNewOption({ value: "", label: "" });
  };

  const handleAddOption = () => {
    if (!newOption.value || !newOption.label) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    createOptionMutation.mutate(
      { id: champ.id, optionData: newOption },
      {
        onSuccess: () => {
          toast.success("Option ajoutée avec succès");
          setNewOption({ value: "", label: "" });
        },
        onError: (error) => {
          toast.error(error.message || "Erreur lors de l'ajout de l'option");
        },
      }
    );
  };

  const handleStartEdit = (option) => {
    setEditingId(option.id);
    setEditForm({ value: option.value, label: option.label });
    setOriginalOption({ value: option.value, label: option.label });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ value: "", label: "" });
    setOriginalOption(null);
  };

  const handleSaveEdit = (optionId) => {
    if (!editForm.value || !editForm.label) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    // Si rien n'a changé, ne pas faire de requête
    if (editForm.value === originalOption.value && editForm.label === originalOption.label) {
      toast.info("Aucune modification détectée");
      handleCancelEdit();
      return;
    }

    updateOptionMutation.mutate(
      { customFieldId: champ.id, optionId, optionData: editForm },
      {
        onSuccess: () => {
          toast.success("Option modifiée avec succès");
          setEditingId(null);
          setEditForm({ value: "", label: "" });
          setOriginalOption(null);
        },
        onError: (error) => {
          toast.error(error.message || "Erreur lors de la modification");
        },
      }
    );
  };

  const handleDeleteOption = (optionId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette option ?")) {
      return;
    }

    deleteOptionMutation.mutate(
      { customFieldId: champ.id, optionId },
      {
        onSuccess: () => {
          toast.success("Option supprimée avec succès");
        },
        onError: (error) => {
          toast.error(error.message || "Erreur lors de la suppression");
        },
      }
    );
  };

  if (!champ) return null;

  const isPending = createOptionMutation.isPending || updateOptionMutation.isPending || deleteOptionMutation.isPending;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Gérer les options : {champ.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table striped bordered hover>
          <thead>
            <tr>
              <th style={{ width: "40%" }}>Valeur</th>
              <th style={{ width: "40%" }}>Label</th>
              <th style={{ width: "20%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {options.map((option) => (
              <tr key={option.id}>
                {editingId === option.id ? (
                  <>
                    <td>
                      <Form.Control
                        type="text"
                        value={editForm.value}
                        onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                        size="sm"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={editForm.label}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                        size="sm"
                      />
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSaveEdit(option.id)}
                          disabled={isPending}
                        >
                          <FaSave />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isPending}
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td><code>{option.value}</code></td>
                    <td>{option.label}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="info"
                          size="sm"
                          onClick={() => handleStartEdit(option)}
                          disabled={isPending}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteOption(option.id)}
                          disabled={isPending}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            <tr className="bg-light">
              <td>
                <Form.Control
                  type="text"
                  value={newOption.value}
                  onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                  placeholder="Nouvelle valeur"
                  size="sm"
                />
              </td>
              <td>
                <Form.Control
                  type="text"
                  value={newOption.label}
                  onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                  placeholder="Nouveau label"
                  size="sm"
                />
              </td>
              <td>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleAddOption}
                  disabled={isPending || !newOption.value || !newOption.label}
                  className="w-100"
                >
                  <FaPlus className="me-1" />
                  Ajouter
                </Button>
              </td>
            </tr>
          </tbody>
        </Table>
        {options.length === 0 && (
          <p className="text-muted text-center">Aucune option pour ce champ</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageFieldOptions;
